/**
 * Envelope normalizer for every backend payload that carries a screen.
 *
 * The renderer was written against ONE envelope — the init script, the `/get`
 * response and the local cache all used to look like this:
 *
 *   { code, status, screen: <id>,
 *     data: { config: { public_url, endpoint_id, endpoint_type, features, ... },
 *             screen: { id, sections, settings },
 *             parameters, variable_data, mode, live_updates, localization } }
 *
 * tnbe-web-be 0.22.x serves a FLAT one:
 *
 *   { code, status,
 *     screen: { id, sections, settings: { ..., mode, live_updates, localization }, cache },
 *     parameters, session_data: { public_url, endpoint_id, endpoint_type },
 *     templates }
 *
 * Five things moved: the `data` wrapper is gone, `screen` holds the screen
 * OBJECT instead of its id, `config` is now `session_data`, `templates` is new,
 * `mode` / `live_updates` /
 * `localization` sank into `screen.settings`, and `cache` climbed from
 * `screen.settings` up onto `screen`. An update (`mode: update`) carries
 * `screen: { id, updates, settings }`: its operations moved from `data.updates`
 * onto `screen`.
 *
 * Every read site in this app (parseNewFormat, the /get sagas, the init-script
 * bootstrap, the config saga) expects the FIRST shape, so normalize INTO it at
 * the trust boundary rather than teaching each read site about both. All three
 * exports are idempotent: an already-canonical payload — including one restored
 * from the local cache, or the legacy `sections`-at-top format — passes through
 * unchanged.
 */
import { substituteTokensDeep } from './designTokens';
import DeviceHelper from './deviceHelper';
import SessionHelper from './sessionHelper';
import { fdsOf, ingestDelivery, resolveDeliveredDesignSystem } from './designSystem';

type AnyRecord = Record<string, any>;

const isPlainObject = (value: any): value is AnyRecord =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/**
 * Keys the 0.22.x `session_data` block carries that USED to live on
 * `data.config` — the endpoint context the app reads for its `Fs*` headers and
 * for `publicUrl`. `session_id` / `device_id` are deliberately NOT in this list:
 * they belong to the page's own session_data, not to config.
 */
const CONFIG_KEYS_FROM_SESSION_DATA = [
  'public_url',
  'endpoint_id',
  'endpoint_type',
  'process_id',
  'task_ref',
  'features',
  'basic',
  'variables',
  'urls',
  'style',
];

/**
 * The flat envelope dropped `features` entirely, and `features.processes !== true`
 * makes the legacy `request` sagas run alongside the action chunks a 0.22.x
 * deployment serves (double-fired requests), and picks the socket.io websocket
 * provider over the native one. So a payload with NO `features` block defaults to
 * actions-mode.
 *
 * The default is applied per-KEY, not per-block: a payload that sends
 * `features.processes` keeps its own value — an explicit `processes: false` is a
 * deliberate legacy-mode deployment, not an omission — while a `features` block
 * that omits `processes` still gets the default, and keeps its other keys.
 * Flip this constant to opt out of the defaulting entirely.
 */
const DEFAULT_PROCESSES_FEATURE = true;

/**
 * Rebuild `data.config` from whichever place this payload keeps the endpoint
 * context. Returns `undefined` when the payload carries no config context at
 * all, so a plain screen response can't accidentally seed a non-empty config
 * state (which would make the config saga skip its `/config` fetch).
 */
const buildConfig = (raw: AnyRecord, body: AnyRecord): AnyRecord | undefined => {
  const sessionData = isPlainObject(raw.session_data) ? raw.session_data : undefined;
  // `config` means two different things depending on the envelope. In the LEGACY
  // shape `data.config` is this endpoint context. In the FLAT shape the
  // top-level `config` is the DESIGN CONFIG (`{{_c.*}}` token values) — a
  // different resource that merely shares the name, and `body` is a copy of
  // `raw`, so reading it here folded design tokens into the endpoint config.
  // Worse, it made this function return non-undefined for a payload with no
  // endpoint context at all, which per the note above makes the config saga skip
  // its `/config` fetch. Only a payload that HAD a `data` wrapper can supply it.
  const isLegacyEnvelope = isPlainObject(raw.data);
  const existingConfig = isLegacyEnvelope && isPlainObject(body.config)
    ? body.config
    : undefined;
  if (!sessionData && !existingConfig) return undefined;

  const config: AnyRecord = {};
  if (sessionData) {
    CONFIG_KEYS_FROM_SESSION_DATA.forEach((key) => {
      if (sessionData[key] !== undefined) config[key] = sessionData[key];
    });
  }
  // `templates` is a top-level sibling in the flat shape; the app reads it off
  // `config.templates`, so fold it back in.
  if (isPlainObject(raw.templates)) config.templates = raw.templates;
  // FE-4517 — `classes` rides along on the same terms: the style-class bodies
  // this screen's nodes name in `style.class`, keyed by external_id. The old
  // envelope carried a global class blob under this name; this is the
  // branch-scoped per-screen map that replaced it, and it IS read now.
  if (isPlainObject(raw.classes)) config.classes = raw.classes;

  // A `session_data` that carries only the page's own session/device ids is NOT
  // config context. Inventing a config from it would seed a non-empty config
  // state at boot, which makes handleConfigRequest skip its `/config` fetch.
  if (!existingConfig && Object.keys(config).length === 0) return undefined;

  // An explicit `data.config` is the canonical shape — it always wins.
  Object.assign(config, existingConfig);

  const features = isPlainObject(config.features) ? config.features : undefined;
  if (DEFAULT_PROCESSES_FEATURE && features?.processes === undefined) {
    config.features = { ...features, processes: true };
  }

  return config;
};

/**
 * FE-4533 — resolve `{{_s.*}}` / `{{_c.*}}` across everything this payload paints.
 * FE-4577 — and merge in whatever the design-system store already holds.
 *
 * Done HERE, at the trust boundary, for the same reason the envelope is
 * normalized here: the alternative is teaching every read site — the screen
 * renderer, the class applier, the template expander — about two namespaces and
 * hoping none is missed. It is also what lets the render seam in `base.ts` stay
 * exactly as it was: by the time `applyStyleClasses(expandTemplateRefs(...))`
 * reads `data.classes` / `data.templates`, those maps are already complete,
 * whether the bodies arrived in this response, in an earlier one, or in a
 * previous session.
 *
 * ORDER IS LOAD-BEARING. Config resolves INSIDE the theme first
 * (`resolveThemeVariables`, now inside `resolveDeliveredDesignSystem`), so
 * `{{_s.accent}}` yields a finished value rather than one that still contains
 * `{{_c.brand_color}}`. See `designTokens.ts`.
 *
 * The three places a token can sit, all verified present in a live payload:
 *   screen.sections   the nodes' own styles
 *   classes           style-class bodies named by `style.class`
 *   templates         template bodies
 *
 * `classes` / `templates` are substituted at the TOP of the body, because that is
 * where every consumer reads them (`base.ts` → `applyStyleClasses(…, data?.classes)`,
 * `index.tsx` → `appData.data.templates`). `buildConfig` also folds the same
 * objects onto `config` for its own readers, so the resolved value is assigned to
 * BOTH — substituting only the `config` copy left the one the renderer actually
 * paints from untouched.
 *
 * NOTE the "no tokens ⇒ return untouched" shortcut this function used to open
 * with is GONE, and deliberately. It tested the PAYLOAD for tokens; once the
 * backend stops re-sending `styles` and `config` to a client that already holds
 * them, every payload declares none and every `{{_s.*}}` would render as literal
 * text. The test now lives in `resolveDeliveredDesignSystem` and is made against
 * the effective scope — payload merged over store.
 */
const resolveDesignTokens = (raw: AnyRecord, body: AnyRecord): AnyRecord => {
  // What THIS payload carried. `buildConfig` has already folded the same two
  // blocks onto `body.config`, so check there too.
  const deliveredBlock = (key: string): AnyRecord | undefined => {
    if (isPlainObject(body[key])) return body[key];
    if (isPlainObject(body.config)) {
      const fromConfig = (body.config as AnyRecord)[key];
      if (isPlainObject(fromConfig)) return fromConfig;
    }
    return undefined;
  };

  const resolved = resolveDeliveredDesignSystem({
    classes: deliveredBlock('classes'),
    templates: deliveredBlock('templates'),
    styles: isPlainObject(raw.styles) ? raw.styles : undefined,
    configTokens: isPlainObject(raw.config) ? raw.config : undefined,
  });

  const substituteScreen = resolved.hasTokens && isPlainObject(body.screen);
  const replaceStyles = isPlainObject(body.styles);
  if (!substituteScreen && !replaceStyles && !resolved.classes && !resolved.templates) {
    return body;
  }

  const next: AnyRecord = { ...body };

  if (substituteScreen) {
    next.screen = substituteTokensDeep(next.screen, resolved.scope);
  }

  // Replace the delivered theme block with the RESOLVED one. Nothing reads it
  // today, but leaving `{{_c.*}}` sitting in the body invites a future consumer
  // to read a token where every sibling holds a value.
  if (replaceStyles) next.styles = resolved.themeVariables;

  if (resolved.classes) next.classes = resolved.classes;
  if (resolved.templates) next.templates = resolved.templates;

  // Keep `config`'s copies pointing at the SAME resolved objects, so the two
  // views of one resource can never disagree.
  if (isPlainObject(next.config) && (resolved.classes || resolved.templates)) {
    const nextConfig: AnyRecord = { ...(next.config as AnyRecord) };
    if (resolved.classes) nextConfig.classes = resolved.classes;
    if (resolved.templates) nextConfig.templates = resolved.templates;
    next.config = nextConfig;
  }

  return next;
};

export type NormalizeOptions = {
  /**
   * Whether this payload is a DELIVERY whose design-system blocks and `Fds`
   * mark should be written into the store.
   *
   * Off by default, because three of `normalizeResponseBody`'s callers are
   * screen-CACHE reads (`sagas.ts:213`, `:479`, `:1069`) — replaying a cached
   * body's stale mark into the store would tell the backend we applied a
   * delivery we did not. `normalizeApiResponse` / `normalizeAppData` turn it on:
   * they only ever see network or bootstrap payloads.
   */
  ingest?: boolean;
  /** The `Fds` RESPONSE header, where a call site can read one. */
  fdsHeader?: string | null;
};

/**
 * FE-4577 — record what this delivery carried.
 *
 * Called BEFORE `buildConfig` and `resolveDesignTokens`, for two reasons: the
 * store must hold RAW bodies (substituting first would bake one theme's values
 * into them permanently), and `buildConfig` overwrites `body.config` with the
 * ENDPOINT config, which is a different thing from the `{{_c.*}}` design tokens
 * under `raw.config`.
 *
 * A payload carrying no `Fds` at all leaves the store untouched — that is an
 * older backend, or the SSR shell, and is handled inside `ingestDelivery`.
 */
const ingestDesignSystemDelivery = (raw: AnyRecord, body: AnyRecord, fdsHeader?: string | null): void => {
  const blockOf = (key: string): AnyRecord | undefined => {
    if (isPlainObject(body[key])) return body[key];
    return isPlainObject(raw[key]) ? raw[key] : undefined;
  };
  const screen = isPlainObject(body.screen) ? body.screen : undefined;
  const settings = isPlainObject(screen?.settings) ? screen.settings : undefined;
  const themeName = typeof settings?.theme === 'string' ? settings.theme : null;

  ingestDelivery({
    fds: fdsOf(raw, fdsHeader),
    classes: blockOf('classes'),
    templates: blockOf('templates'),
    // `styles` and `config` are read off `raw` exactly as `resolveDesignTokens`
    // reads them, so the store holds precisely what the resolver would have used.
    styles: isPlainObject(raw.styles) ? raw.styles : undefined,
    configTokens: isPlainObject(raw.config) ? raw.config : undefined,
    themeName,
  });
};

/**
 * Normalize any screen-bearing payload to the canonical `data` BODY —
 * what `parseNewFormat` / `parseOldFormat` and the cache writers consume.
 */
export const normalizeResponseBody = (raw: any, opts?: NormalizeOptions): any => {
  if (!isPlainObject(raw)) return raw;

  // A `data` that is NOT an object is the body itself — an action can answer
  // with `{ code, data: [...] }` or `{ code, data: "text" }`, and those flow
  // into `$response` verbatim. Same semantics as the `'data' in json ? …` probe
  // this replaced; there is nothing to normalize on a non-object body.
  if ('data' in raw && !isPlainObject(raw.data)) return raw.data;

  // Legacy envelope: the body already sits under `data`. Flat envelope: the
  // body IS the response.
  //
  // `code` / `status` are deliberately NOT stripped in the flat branch: a
  // process/action response with no `data` key was previously passed through
  // whole and lands in the `$response` context, so `{{$response.status}}` and
  // `{{$response.code}}` must keep resolving. No parser reads them off a body.
  const body: AnyRecord = isPlainObject(raw.data) ? { ...raw.data } : { ...raw };

  let screen: AnyRecord | undefined;
  if (isPlainObject(body.screen)) screen = body.screen;
  else if (isPlainObject(raw.screen)) screen = raw.screen;

  if (screen) {
    const settings = isPlainObject(screen.settings) ? screen.settings : undefined;

    // 0.22.x sank these three from the body into `screen.settings`. Hoist them
    // back: `mode` drives the update-vs-show branch in the sagas, `live_updates`
    // gates the websocket, `localization` sets <html lang>.
    if (body.mode === undefined && settings?.mode !== undefined) {
      body.mode = settings.mode;
    }
    if (body.live_updates === undefined && settings?.live_updates !== undefined) {
      body.live_updates = settings.live_updates;
    }
    if (body.localization === undefined) {
      const localization = settings?.localization ?? screen.localization;
      if (localization !== undefined) body.localization = localization;
    }

    // ...and `cache` climbed the other way, out of `settings` onto `screen`,
    // while every read site is `settings?.cache` (the local-cache write gate).
    body.screen = (screen.cache !== undefined && settings && settings.cache === undefined)
      ? { ...screen, settings: { ...settings, cache: screen.cache } }
      : screen;
  }

  if (opts?.ingest) ingestDesignSystemDelivery(raw, body, opts.fdsHeader);

  const config = buildConfig(raw, body);
  if (config) body.config = config;
  else delete body.config;

  const resolved = resolveDesignTokens(raw, body);

  // FE-4892 — an update's `insert` / `remove` / `replace` moved off the body onto
  // `screen.updates`, while `updateDataWithModeUpdate` reads `updates` off the
  // body: without this hoist a `mode: update` merged only its `parameters`.
  // Hoisted AFTER token resolution, from the resolved `screen`, so a node the
  // update brings in gets its `{{_s.*}}` / `{{_c.*}}` substituted like every
  // other node on the screen.
  const resolvedScreen = isPlainObject(resolved.screen) ? resolved.screen : undefined;
  if (resolved.updates === undefined && resolvedScreen?.updates !== undefined) {
    resolved.updates = resolvedScreen.updates;
  }

  return resolved;
};

/**
 * Backfill the client-owned ids onto a response's `session_data`. The flat
 * envelope reuses that block for the endpoint context, so it no longer carries
 * `session_id` / `device_id` — and PAGE_SUCCESS REPLACES `page.session_data`,
 * which would drop the ids the live-updates websocket is keyed on. Anything the
 * server does send still wins.
 */
export const normalizeSessionData = (sessionData: any): AnyRecord => ({
  device_id: DeviceHelper.getOrCreateDeviceId(),
  session_id: SessionHelper.getOrCreateSessionId(),
  ...(isPlainObject(sessionData) ? sessionData : {}),
});

/**
 * Normalize a full API response to the canonical `{ code, status, data,
 * session_data }` the sagas destructure.
 */
export const normalizeApiResponse = (raw: any, opts?: NormalizeOptions): {
  code: any;
  status: any;
  data: any;
  session_data: AnyRecord;
} => {
  if (!isPlainObject(raw)) {
    return { code: undefined, status: undefined, data: raw, session_data: normalizeSessionData(undefined) };
  }

  let rawSessionData: AnyRecord | undefined;
  if (isPlainObject(raw.session_data)) rawSessionData = raw.session_data;
  else if (isPlainObject(raw.data) && isPlainObject(raw.data.session_data)) rawSessionData = raw.data.session_data;

  return {
    code: raw.code,
    status: raw.status,
    data: normalizeResponseBody(raw, { ingest: true, ...opts }),
    session_data: normalizeSessionData(rawSessionData),
  };
};

/**
 * Normalize an init-script / cold-boot payload to the canonical envelope:
 * `screen` back to the screen ID, everything else under `data`.
 */
export const normalizeAppData = (raw: any, opts?: NormalizeOptions): AnyRecord | null => {
  if (!isPlainObject(raw)) return null;

  // The SSR shell carries no `Fds`, so ingesting it is a no-op (rule 8); the
  // retry that `retryWithHeaders` performs DOES carry one and must be ingested.
  const data = normalizeResponseBody(raw, { ingest: true, ...opts });
  // `screen` is polymorphic across the two shapes: an id in the legacy
  // envelope, the screen object in the flat one.
  const screenId = isPlainObject(raw.screen)
    ? raw.screen.id
    : raw.screen ?? data?.screen?.id ?? data?.screen_id;

  return {
    ...(raw.apiUrl !== undefined ? { apiUrl: raw.apiUrl } : {}),
    ...(raw.code !== undefined ? { code: raw.code } : {}),
    ...(raw.status !== undefined ? { status: raw.status } : {}),
    screen: screenId,
    data,
  };
};
